(() => {
  const drawerRequestParam = "open-cart";

  const normalizedPath = (path) => path.replace(/\/$/, "") || "/";

  const cartPath = () => {
    const cartUrl = new URL(
      window.theme.routes.cart_url,
      window.location.origin,
    );
    return normalizedPath(cartUrl.pathname);
  };

  const redirectCartPageToDrawer = () => {
    if (
      window.theme.settings.cartType !== "modal" ||
      normalizedPath(window.location.pathname) !== cartPath() ||
      window.Shopify?.designMode
    ) {
      return false;
    }

    const url = new URL(window.location.href);
    const rootUrl = new URL(
      window.theme.routes.root_url,
      window.location.origin,
    );
    url.pathname = rootUrl.pathname;
    url.searchParams.set(drawerRequestParam, "true");
    window.location.replace(url.toString());
    return true;
  };

  if (redirectCartPageToDrawer()) return;

  const showCartError = (message) => {
    const errorElement = document.getElementById("cart-errors");

    if (errorElement) {
      errorElement.textContent = message;
    }
  };

  const refreshCart = () =>
    new Promise((resolve, reject) => {
      let timeoutId;

      const cleanup = () => {
        window.removeEventListener("shapes:cart:update", handleUpdate);
        document.removeEventListener("theme:cart:error:other", handleError);
        window.clearTimeout(timeoutId);
      };
      const handleUpdate = () => {
        cleanup();
        resolve();
      };
      const handleError = () => {
        cleanup();
        reject(new Error("Unable to refresh cart"));
      };

      window.addEventListener("shapes:cart:update", handleUpdate, {
        once: true,
      });
      document.addEventListener("theme:cart:error:other", handleError, {
        once: true,
      });
      timeoutId = window.setTimeout(handleError, 5000);
      document.dispatchEvent(new CustomEvent("theme:update:cart"));
    });

  const refreshCartOrReload = async () => {
    try {
      await refreshCart();
    } catch (error) {
      console.error(error);
      window.location.reload();
    }
  };

  const cartUpdateUrl = () => {
    const updateUrl = window.theme.routes.cart_update_url;
    return updateUrl.endsWith(".js") ? updateUrl : `${updateUrl}.js`;
  };

  const updateCart = async (payload) => {
    const response = await fetch(cartUpdateUrl(), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    const cart = await response.json();

    if (!response.ok) {
      throw new Error(cart.description || "Unable to update cart");
    }

    return cart;
  };

  const discountCodeTitles = (cart) => {
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes
        .filter((discountCode) => discountCode.applicable)
        .map((discountCode) => discountCode.code.trim());
    }

    const applications = [
      ...(cart.cart_level_discount_applications || []).map(
        (application) => application.discount_application || application,
      ),
      ...(cart.items || []).flatMap((item) =>
        (item.line_level_discount_allocations || []).map(
          (allocation) => allocation.discount_application,
        ),
      ),
    ];

    return [
      ...new Set(
        applications
          .filter((application) => application?.type === "discount_code")
          .map((application) => application.title.trim()),
      ),
    ];
  };

  const normalizedCode = (code) => code.trim().toLocaleLowerCase();

  const currentDiscountCodes = (container) =>
    Array.from(
      container?.querySelectorAll("[data-applied-discount-code]") || [],
    ).map((element) => element.textContent.trim());

  const setDiscountBusy = (container, busy) => {
    container
      ?.querySelectorAll("input, button")
      .forEach((control) => (control.disabled = busy));
    container?.setAttribute("aria-busy", busy.toString());
  };

  const showDiscountStatus = (container, message) => {
    const status = container?.querySelector("[data-discount-status]");

    if (status) {
      status.textContent = message;
      status.hidden = false;
    }
  };

  const applyDiscount = async (form) => {
    const container = form.closest("[data-cart-discounts]");
    const input = form.querySelector("[data-discount-code-input]");
    const code = input?.value.trim();

    if (!code) {
      showDiscountStatus(container, "Enter a discount code.");
      input?.focus();
      return;
    }

    setDiscountBusy(container, true);

    try {
      const requestedCodes = [...currentDiscountCodes(container), code];
      const cart = await updateCart({ discount: requestedCodes.join(",") });
      const appliedCodes = discountCodeTitles(cart);
      const codeApplied = appliedCodes.some(
        (appliedCode) => normalizedCode(appliedCode) === normalizedCode(code),
      );

      if (!codeApplied) {
        showDiscountStatus(
          container,
          "That code could not be applied to this cart.",
        );
        return;
      }

      showDiscountStatus(container, "Discount code applied.");
      await refreshCartOrReload();
    } catch (error) {
      console.error(error);
      showDiscountStatus(
        container,
        "The discount code could not be applied. Try again.",
      );
    } finally {
      setDiscountBusy(container, false);
    }
  };

  const removeDiscounts = async (button) => {
    const container = button.closest("[data-cart-discounts]");
    setDiscountBusy(container, true);

    try {
      const cart = await updateCart({ discount: "" });

      if (discountCodeTitles(cart).length > 0) {
        throw new Error("Shopify did not remove every discount code");
      }

      showDiscountStatus(container, "Discount removed.");
      await refreshCartOrReload();
    } catch (error) {
      console.error(error);
      showDiscountStatus(
        container,
        "The discount code could not be removed. Try again.",
      );
    } finally {
      setDiscountBusy(container, false);
    }
  };

  const removeBox = async (button) => {
    const box = button.closest("[data-boxi-box]");
    const boxAttributeKey = box?.dataset.boxiAttributeKey;
    const lineKeys = Array.from(
      box?.querySelectorAll("[data-boxi-line-key]") || [],
    ).map((input) => input.value);

    if (!lineKeys.length) return;

    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    try {
      const updates = Object.fromEntries(lineKeys.map((key) => [key, 0]));
      const attributes = boxAttributeKey ? { [boxAttributeKey]: "" } : {};
      await updateCart({ updates, attributes });
      refreshCart();
    } catch (error) {
      console.error(error);
      showCartError(window.theme.strings.cartError);
      button.disabled = false;
      button.removeAttribute("aria-busy");
    }
  };

  const isPlainPrimaryClick = (event) =>
    event.button === 0 &&
    !event.defaultPrevented &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey;

  const handleDocumentClick = (event) => {
    const removeBoxButton = event.target.closest("[data-remove-box]");
    const removeDiscountsButton = event.target.closest(
      "[data-remove-discounts]",
    );

    if (removeBoxButton) {
      event.preventDefault();
      removeBox(removeBoxButton);
      return;
    }

    if (removeDiscountsButton) {
      event.preventDefault();
      removeDiscounts(removeDiscountsButton);
      return;
    }

    if (
      window.theme.settings.cartType !== "modal" ||
      !isPlainPrimaryClick(event)
    ) {
      return;
    }

    const link = event.target.closest("a[href]");
    if (!link || link.target === "_blank" || link.hasAttribute("download")) {
      return;
    }

    const url = new URL(link.href, window.location.href);
    if (
      url.origin !== window.location.origin ||
      normalizedPath(url.pathname) !== cartPath()
    ) {
      return;
    }

    event.preventDefault();
    document.dispatchEvent(new CustomEvent("theme:open:cart-drawer"));
  };

  document.addEventListener("click", handleDocumentClick, true);

  document.addEventListener("submit", (event) => {
    const discountForm = event.target.closest("[data-cart-discount-form]");

    if (!discountForm) return;

    event.preventDefault();
    applyDiscount(discountForm);
  });

  window.ElectricCart = {
    consumeDrawerRequest() {
      const url = new URL(window.location.href);

      if (url.searchParams.get(drawerRequestParam) !== "true") {
        return false;
      }

      url.searchParams.delete(drawerRequestParam);
      window.history.replaceState({}, "", url);
      return true;
    },
  };
})();
